import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getArticles, getCategories, getSiteSettings, getPodcasts, getShorts, getLiveStocks, getLiveCricket } from '../services/api';
import { ChevronRight, ChevronLeft, Play, Headphones, Flame, BarChart3, X } from 'lucide-react';
import SectionHeader from '../components/news/SectionHeader';
import ElectionPanel from '../components/news/ElectionPanel';
import QuickEditButton from '../components/news/QuickEditButton';

const FB = 'https://images.unsplash.com/photo-1596796679119-7cf1a9e5448b?q=80&w=800';
// Diverse fallback images per category so broken images don't all look the same
const CAT_FALLBACKS = {
  cat_politics: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?q=80&w=800',
  cat_business: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=800',
  cat_sports: 'https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?q=80&w=800',
  cat_entertainment: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=800',
  cat_technology: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=800',
  cat_culture: 'https://images.unsplash.com/photo-1590050752117-238cb0fb12b1?q=80&w=800',
  cat_local: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?q=80&w=800',
};
const getFB = (catId) => CAT_FALLBACKS[catId] || FB;
const oE = (e, catId) => { const fb = getFB(catId); if (e?.target && e.target.src !== fb) e.target.src = fb; };
const CC = { cat_politics:'#d32f2f', cat_business:'#1565c0', cat_sports:'#2e7d32', cat_entertainment:'#7b1fa2', cat_technology:'#0277bd', cat_culture:'#e65100', cat_local:'#00695c', cat_7b395a57:'#455a64' };

// Safe accessors - never throw on null/undefined article
const gT = (a, isKn) => (a && (isKn ? (a.title_kn || a.title) : a.title)) || '';
const gS = (a, isKn) => (a && (isKn ? (a.summary_kn || a.summary) : a.summary)) || '';
const gC = (a, categories, isKn) => {
  if (!a || !Array.isArray(categories)) return '';
  const c = categories.find(x => x?.category_id === a.category_id);
  return c ? (isKn ? (c.name_kn || c.name) : c.name) : '';
};

function ago(d, kn) {
  if (!d) return '';
  try {
    const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (isNaN(m) || m < 0) return '';
    if (m < 60) return kn ? `${m} ನಿಮಿಷಗಳ ಹಿಂದೆ` : `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return kn ? `${h} ಗಂಟೆಗಳ ಹಿಂದೆ` : `${h}h ago`;
    return kn ? `${Math.floor(h/24)} ದಿನಗಳ ಹಿಂದೆ` : `${Math.floor(h/24)}d ago`;
  } catch { return ''; }
}

/* ─── BREAKING TICKER ─── */
function Ticker({ settings }) {
  const { isKannada } = useLanguage();
  const items = Array.isArray(settings?.items) ? settings.items : [];
  if (!settings?.enabled || !items.length) return null;
  const txt = items.map(i => (isKannada ? (i?.text_kn || i?.text_en) : i?.text_en) || '').filter(Boolean).join('  |  ');
  if (!txt) return null;
  return (
    <div className="bg-red-600 text-white flex items-center overflow-hidden" data-testid="breaking-ticker">
      <div className="bg-red-700 px-4 py-2 text-xs font-bold whitespace-nowrap shrink-0 flex items-center gap-1.5">
        <Flame className="w-3.5 h-3.5" /> {isKannada ? 'ತಾಜಾ ಸುದ್ದಿ' : 'BREAKING'}
      </div>
      <div className="overflow-hidden flex-1">
        <div className="animate-marquee whitespace-nowrap py-2 px-4 text-[13px]">{txt}  |  {txt}</div>
      </div>
    </div>
  );
}

/* ─── HERO SECTION: Large left slider + sidebar right (auto-rotating carousel) ─── */
function HeroSection({ articles, categories, sliderCfg }) {
  const { isKannada } = useLanguage();
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  const safeAll = (articles || []).filter(a => a?.article_id);
  // Main panel: up to 7 slider articles (admin-selected else first 7)
  const sliderIds = Array.isArray(sliderCfg?.article_ids) ? sliderCfg.article_ids : [];
  const sliderArticles = sliderIds.length
    ? sliderIds.map(id => safeAll.find(a => a.article_id === id)).filter(Boolean).slice(0, 7)
    : safeAll.slice(0, 7);
  const autoplay = sliderCfg?.autoplay !== false;
  const interval = Math.max(2000, Number(sliderCfg?.interval_ms) || 5000);

  useEffect(() => {
    if (!autoplay || sliderArticles.length <= 1) return;
    timerRef.current = setInterval(() => setIdx(i => (i + 1) % sliderArticles.length), interval);
    return () => clearInterval(timerRef.current);
  }, [autoplay, interval, sliderArticles.length]);

  if (!safeAll.length || !sliderArticles.length) return null;

  // Side panel: 6 recent, skipping anything already in slider
  const sliderSet = new Set(sliderArticles.map(a => a.article_id));
  const side = safeAll.filter(a => !sliderSet.has(a.article_id)).slice(0, 6);

  const a = sliderArticles[Math.min(idx, sliderArticles.length - 1)];
  const prev = () => setIdx(i => (i - 1 + sliderArticles.length) % sliderArticles.length);
  const next = () => setIdx(i => (i + 1) % sliderArticles.length);

  return (
    <div className="grid grid-cols-12 gap-5 mt-5" data-testid="hero-section">
      {/* Main hero slider */}
      <div className="col-span-12 lg:col-span-8">
        <div className="relative rounded-xl overflow-hidden bg-black shadow-lg" data-testid="hero-slider">
          <Link to={`/article/${a.slug || a.article_id}`} className="block relative group" key={a.article_id} data-testid="hero-main">
            <img
              src={a.featured_image || getFB(a.category_id)}
              alt=""
              className="w-full aspect-[3.5/4] sm:aspect-[16/9] object-cover"
              onError={(e) => oE(e, a.category_id)}
            />
            <div className="absolute inset-x-0 bottom-0 h-[35%] sm:h-auto sm:inset-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent sm:from-black/90 sm:via-black/30" />
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-7 text-white min-h-[28%] sm:min-h-0 flex flex-col justify-end">
              <span className="inline-block self-start px-2.5 py-1 text-[11px] font-bold rounded mb-2" style={{ backgroundColor: CC[a.category_id] || '#d32f2f' }}>
                {gC(a, categories, isKannada)}
              </span>
              <h1 className="font-kannada font-black text-white drop-shadow-lg line-clamp-3 sm:line-clamp-2" data-testid="hero-title slider-title" style={{ fontSize: 'clamp(18px,2.6vw,30px)', lineHeight: 1.2 }}>
                {gT(a, isKannada)}
              </h1>
              <p className="hidden sm:block mt-1.5 text-white/90 line-clamp-2 max-w-3xl font-baloo" style={{ fontSize: '14px' }}>
                {gS(a, isKannada)}
              </p>
            </div>
          </Link>
          {sliderArticles.length > 1 && (
            <>
              <button type="button" onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white text-black flex items-center justify-center shadow" aria-label="Previous" data-testid="slider-prev">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button type="button" onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white text-black flex items-center justify-center shadow" aria-label="Next" data-testid="slider-next">
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute bottom-3 right-4 flex gap-1.5">
                {sliderArticles.map((_, i) => (
                  <button key={i} type="button" onClick={() => setIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-white' : 'w-3 bg-white/50 hover:bg-white/80'}`}
                    aria-label={`Slide ${i + 1}`} data-testid={`slider-dot-${i}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sidebar: Recent news */}
      <div className="col-span-12 lg:col-span-4" data-testid="hero-sidebar">
        <div className="bg-red-600 text-white px-4 py-2.5 rounded-t-lg">
          <h3 className="text-[15px] font-bold">{isKannada ? 'ಇತ್ತೀಚಿನ ಸುದ್ದಿಗಳು' : 'Recent News'}</h3>
        </div>
        <div className="bg-white border border-gray-200 border-t-0 rounded-b-lg divide-y divide-gray-100">
          {side.map(r => r?.article_id ? (
            <Link key={r.article_id} to={`/article/${r.slug || r.article_id}`} className="flex gap-3 p-3 hover:bg-gray-50 transition-colors group" data-testid={`recent-${r.article_id}`}>
              <div className="w-20 h-16 rounded overflow-hidden shrink-0">
                <img src={r.featured_image || getFB(r.category_id)} alt="" className="w-full h-full object-cover" onError={(e) => oE(e, r.category_id)} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-[13px] font-bold text-[#1a1a1a] line-clamp-2 leading-snug group-hover:text-red-600 transition-colors font-kannada">{gT(r, isKannada)}</h4>
              </div>
            </Link>
          ) : null)}
        </div>
      </div>
    </div>
  );
}

/* ─── Section header is now imported from shared components ─── */

/* Apply client-side filter based on active tab */
function filterBySectionTab(articles, tab) {
  const safe = (articles || []).filter(a => a?.article_id);
  if (tab === 'featured') {
    const feat = safe.filter(a => a.featured);
    return feat.length ? feat : safe;
  }
  if (tab === 'trending') {
    return safe.slice().sort((a, b) => (b.views || 0) - (a.views || 0));
  }
  return safe.slice().sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0));
}

/* ─── GRID CARD: single article card used in category grids ─── */
function GridCard({ article, categories, accent, isKannada }) {
  if (!article?.article_id) return null;
  return (
    <div className="relative group" data-testid={`card-wrap-${article.article_id}`}>
      <QuickEditButton articleId={article.article_id} />
      <Link
        to={`/article/${article.slug || article.article_id}`}
        className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-[0_10px_28px_-14px_rgba(0,0,0,0.22)] transition-all flex flex-col block"
        data-testid={`card-${article.article_id}`}
      >
        <div className="relative aspect-video overflow-hidden">
          <img
            src={article.featured_image || getFB(article.category_id)}
            alt=""
            className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
            onError={(e) => oE(e, article.category_id)}
          />
          <span
            className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold text-white rounded"
            style={{ backgroundColor: accent }}
          >
            {gC(article, categories, isKannada)}
          </span>
        </div>
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="text-[15px] font-bold text-[#1a1a1a] leading-snug line-clamp-2 font-kannada group-hover:text-[color:var(--accent)]" style={{ '--accent': accent }}>
            {gT(article, isKannada)}
          </h3>
          {gS(article, isKannada) && (
            <p className="text-[12.5px] text-gray-500 mt-1.5 line-clamp-2 leading-relaxed">{gS(article, isKannada)}</p>
          )}
        </div>
      </Link>
    </div>
  );
}

/* ─── CATEGORY SECTION: standardized tab + 3-column grid of up to 6 cards ─── */
function CategorySection({ title, articles, categories, catId }) {
  const { isKannada } = useLanguage();
  const [tab, setTab] = useState('latest');
  const safe = (articles || []).filter(a => a?.article_id);
  if (!safe.length) return null;
  const items = filterBySectionTab(safe, tab).slice(0, 6);
  if (!items.length) return null;

  const accent = CC[catId] || '#d32f2f';

  return (
    <section className="mt-10" data-testid={`section-${catId || 'mixed'}`}>
      <SectionHeader title={title} catId={catId} activeTab={tab} onTabChange={setTab} accent={accent} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(a => (
          <GridCard key={a.article_id} article={a} categories={categories} accent={accent} isKannada={isKannada} />
        ))}
      </div>
    </section>
  );
}

/* ─── CATEGORY + POPULAR: grid of cards + popular sidebar ─── */
function NationalWithPopular({ title, catId, articles, categories, allArticles }) {
  const { isKannada } = useLanguage();
  const [tab, setTab] = useState('latest');
  const safe = (articles || []).filter(a => a?.article_id);
  const items = filterBySectionTab(safe, tab).slice(0, 6);
  const popular = (allArticles || []).filter(a => a?.article_id && a?.featured).slice(0, 6);
  if (!items.length && !popular.length) return null;

  const accent = CC[catId] || '#d32f2f';

  return (
    <section className="mt-10" data-testid={`section-popular-${catId || 'mixed'}`}>
      <SectionHeader title={title} catId={catId} activeTab={tab} onTabChange={setTab} accent={accent} />
      <div className="grid grid-cols-12 gap-5">
        {/* 6-card grid */}
        <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(a => (
            <GridCard key={a.article_id} article={a} categories={categories} accent={accent} isKannada={isKannada} />
          ))}
        </div>

        {/* Popular sidebar */}
        {popular.length > 0 && (
          <div className="col-span-12 lg:col-span-4">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm h-full">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full" style={{ backgroundColor: accent }} />
                <h3 className="text-[16px] font-black text-[#1a1a1a] font-kannada">{isKannada ? 'ಜನಪ್ರಿಯ' : 'Popular'}</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {popular.map((a, i) => (
                  <Link key={a.article_id} to={`/article/${a.slug || a.article_id}`} className="flex items-start gap-3 p-3 hover:bg-gray-50 group" data-testid={`popular-${i}`}>
                    <span className="text-[22px] font-black leading-none shrink-0" style={{ color: `${accent}33` }}>{i + 1}</span>
                    <div className="min-w-0">
                      <h4 className="text-[13px] font-bold text-[#1a1a1a] line-clamp-2 leading-snug font-kannada">{gT(a, isKannada)}</h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── VIDEO POPUP MODAL ─── */
function VideoModal({ video, onClose }) {
  if (!video) return null;
  const url = video.video_url || video.embed_url || '';
  const isYT = /youtube\.com|youtu\.be/i.test(url);
  const isInsta = /instagram\.com/i.test(url);
  let embedSrc = '';
  if (isYT) {
    const vid = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([a-zA-Z0-9_-]{6,})/)?.[1];
    embedSrc = vid ? `https://www.youtube.com/embed/${vid}?autoplay=1` : '';
  } else if (isInsta) {
    embedSrc = url.replace(/\/?$/, '/embed/');
  }
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm" onClick={onClose} data-testid="video-popup">
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25" data-testid="video-popup-close"><X className="w-5 h-5" /></button>
      <div className="relative w-full max-w-[420px] aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {embedSrc ? (
          <iframe src={embedSrc} className="w-full h-full" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen title="video" />
        ) : url ? (
          <video src={url} controls autoPlay className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">No playable source</div>
        )}
      </div>
    </div>
  );
}

/* ─── PODCAST POPUP MODAL ─── */
function PodcastModal({ pod, onClose }) {
  if (!pod) return null;
  const url = pod.audio_url || pod.embed_url || '';
  const isSpotify = /spotify\.com/i.test(url);
  const isYT = /youtube\.com|youtu\.be/i.test(url);
  let embedSrc = '';
  if (isSpotify) embedSrc = url.replace('open.spotify.com/', 'open.spotify.com/embed/');
  else if (isYT) {
    const vid = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{6,})/)?.[1];
    embedSrc = vid ? `https://www.youtube.com/embed/${vid}?autoplay=1` : '';
  }
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm" onClick={onClose} data-testid="podcast-popup">
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25" data-testid="podcast-popup-close"><X className="w-5 h-5" /></button>
      <div className="relative w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {pod.thumbnail && <img src={pod.thumbnail} alt="" className="w-full aspect-video object-cover" />}
        <div className="p-4">
          <h3 className="text-base font-black text-[#1a1a1a] font-kannada mb-3">{pod.title_kn || pod.title}</h3>
          {embedSrc ? (
            <iframe src={embedSrc} width="100%" height={isSpotify ? 232 : 200} frameBorder="0" allow="autoplay; encrypted-media" title="podcast" />
          ) : pod.audio_url ? (
            <audio src={pod.audio_url} controls autoPlay className="w-full" />
          ) : (
            <p className="text-sm text-gray-500">No audio source available</p>
          )}
          <Link to={`/podcast/${pod.podcast_id}`} className="text-xs font-bold text-purple-600 hover:underline mt-3 inline-block" onClick={onClose}>Open full episode →</Link>
        </div>
      </div>
    </div>
  );
}

/* ─── VIDEO NEWS (REEL STYLE) ─── */
function VideoSection({ shorts }) {
  const { isKannada } = useLanguage();
  const [activeVideo, setActiveVideo] = useState(null);
  const safe = (shorts || []).filter(s => s?.short_id);
  if (!safe.length) return null;
  const handleCardClick = (s, e) => {
    // Click on play icon -> popup. Click anywhere else on card -> link if available.
    const link = s.video_url || s.embed_url;
    if (link && !e?.defaultPrevented) {
      window.open(link, '_blank', 'noopener');
    }
  };
  return (
    <section className="mt-8" data-testid="video-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[22px] font-black text-[#1a1a1a] font-kannada">{isKannada ? 'ವೀಡಿಯೊ ಸುದ್ದಿಗಳು' : 'Video News'}</h2>
        <Link to="/videos" className="text-[13px] font-bold text-red-600 hover:underline flex items-center gap-0.5" data-testid="more-videos">
          {isKannada ? 'ಎಲ್ಲಾ ವೀಡಿಯೊಗಳು' : 'All Videos'} <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {safe.slice(0, 5).map(s => (
          <div
            key={s.short_id}
            className="group bg-black rounded-xl overflow-hidden cursor-pointer relative hover:shadow-xl transition-shadow"
            data-testid={`video-${s.short_id}`}
            onClick={(e) => handleCardClick(s, e)}
          >
            {/* Reel aspect 9:16 */}
            <div className="relative aspect-[9/16]">
              {s.thumbnail ? (
                <img src={s.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" onError={(e) => oE(e)} />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-700 to-black flex items-center justify-center"><Play className="w-10 h-10 text-white/40" /></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
              {/* Play button -> opens popup */}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveVideo(s); }}
                className="absolute inset-0 flex items-center justify-center"
                aria-label="Play video"
                data-testid={`video-play-${s.short_id}`}
              >
                <span className="w-14 h-14 rounded-full bg-red-600/90 group-hover:bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-all">
                  <Play className="w-6 h-6 text-white ml-1" fill="currentColor" />
                </span>
              </button>
              {/* Title at bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                <h4 className="text-[12.5px] font-bold line-clamp-2 leading-snug font-kannada">{isKannada ? (s.title_kn || s.title) : s.title}</h4>
              </div>
            </div>
          </div>
        ))}
      </div>
      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
    </section>
  );
}

/* ─── PODCAST SECTION ─── */
function PodcastSection({ podcasts }) {
  const { isKannada } = useLanguage();
  const [activePod, setActivePod] = useState(null);
  const safe = (podcasts || []).filter(p => p?.podcast_id);
  if (!safe.length) return null;
  return (
    <section className="mt-8" data-testid="podcast-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[22px] font-black text-[#1a1a1a] font-kannada flex items-center gap-2">
          <Headphones className="w-5 h-5 text-purple-600" /> {isKannada ? 'ಪಾಡ್‌ಕಾಸ್ಟ್' : 'Podcasts'}
        </h2>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
        {safe.map(p => (
          <div
            key={p.podcast_id}
            className="min-w-[200px] max-w-[200px] bg-white border border-gray-100 rounded-lg overflow-hidden shrink-0 hover:shadow-md transition-shadow group cursor-pointer"
            data-testid={`podcast-${p.podcast_id}`}
            onClick={() => { window.location.href = `/podcast/${p.podcast_id}`; }}
          >
            <div className="relative">
              {p.thumbnail ? <img src={p.thumbnail} alt="" className="w-full aspect-square object-cover" onError={(e) => oE(e)} /> : <div className="w-full aspect-square bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center"><Headphones className="w-12 h-12 text-white/70" /></div>}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActivePod(p); }}
                className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-purple-600 text-white flex items-center justify-center shadow group-hover:scale-110 transition-transform hover:bg-purple-700"
                aria-label="Play podcast"
                data-testid={`podcast-play-${p.podcast_id}`}
              >
                <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
              </button>
            </div>
            <div className="p-3">
              <h4 className="text-[13px] font-bold text-[#1a1a1a] line-clamp-2 leading-snug font-kannada group-hover:text-purple-600 transition-colors">{isKannada ? (p.title_kn || p.title) : p.title}</h4>
              {p.duration && <span className="text-[11px] text-gray-400 mt-1 block">{p.duration}</span>}
            </div>
          </div>
        ))}
      </div>
      <PodcastModal pod={activePod} onClose={() => setActivePod(null)} />
    </section>
  );
}

/* ─── WIDGETS BAR (Stocks + Cricket) ─── */
function WidgetsBar({ stocks, cricket }) {
  const safeStocks = Array.isArray(stocks) ? stocks.filter(s => s?.name) : [];
  const safeCricket = Array.isArray(cricket) ? cricket.filter(m => m?.team1) : [];
  if (!safeStocks.length && !safeCricket.length) return null;
  return (
    <div className="flex items-center gap-4 overflow-x-auto py-2 px-1 mt-4 bg-white border border-gray-200 rounded-lg scrollbar-thin" data-testid="widgets-bar">
      {safeStocks.map(s => (
        <div key={s.name} className="flex items-center gap-2 px-3 py-1 shrink-0 border-r border-gray-200 last:border-0">
          <BarChart3 className="w-3.5 h-3.5 text-blue-500" />
          <span className="text-[11px] font-bold text-gray-600">{s.name}</span>
          <span className="text-[12px] font-bold text-[#1a1a1a]">{s.value}</span>
          <span className={`text-[10px] font-bold ${s.up ? 'text-green-600' : 'text-red-600'}`}>{s.change}</span>
        </div>
      ))}
      {safeCricket.slice(0, 2).map((m, i) => (
        <div key={i} className="flex items-center gap-2 px-3 py-1 shrink-0 border-r border-gray-200 last:border-0">
          <span className="text-[11px]">🏏</span>
          <span className="text-[11px] font-bold">{m.team1} {m.score1}</span>
          <span className="text-[10px] text-gray-400">vs</span>
          <span className="text-[11px] font-bold">{m.team2} {m.score2}</span>
          {m.live && <span className="px-1 py-0.5 text-[8px] font-bold bg-red-600 text-white rounded animate-pulse">LIVE</span>}
        </div>
      ))}
    </div>
  );
}

/* ═════════════ HOMEPAGE ═════════════ */
export default function HomePage() {
  const { isKannada } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [podcasts, setPodcasts] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [cricket, setCricket] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [artR, catR, setR, podR, shR] = await Promise.all([
          getArticles({ limit: 100 }).catch(() => ({ data: { articles: [] } })),
          getCategories().catch(() => ({ data: [] })),
          getSiteSettings().catch(() => ({ data: null })),
          getPodcasts({ limit: 10, status: 'published' }).catch(() => ({ data: { podcasts: [] } })),
          getShorts({ limit: 10, status: 'published' }).catch(() => ({ data: { shorts: [] } })),
        ]);
        // Defensive: ensure arrays, filter out any malformed entries
        const rawArticles = Array.isArray(artR?.data?.articles) ? artR.data.articles : [];
        setArticles(rawArticles.filter(a => a && a.article_id));
        const rawCats = Array.isArray(catR?.data) ? catR.data : [];
        setCategories(rawCats.filter(c => c && c.category_id));
        setSettings(setR?.data || null);
        setPodcasts(Array.isArray(podR?.data?.podcasts) ? podR.data.podcasts : []);
        setShorts(Array.isArray(shR?.data?.shorts) ? shR.data.shorts : []);
        getLiveStocks().then(r => setStocks(Array.isArray(r?.data?.stocks) ? r.data.stocks : [])).catch(() => {});
        getLiveCricket().then(r => setCricket(Array.isArray(r?.data?.matches) ? r.data.matches : [])).catch(() => {});
      } catch (e) {
        console.error('[HomePage] load error:', e);
      } finally { setLoading(false); }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center" data-testid="homepage-loading"><div className="w-8 h-8 border-3 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;

  // Multi-category aware: an article appears in a category if either its primary
  // category_id OR any id in its category_ids array matches.
  const byCat = (id) => articles.filter(a =>
    a.category_id === id || (Array.isArray(a.category_ids) && a.category_ids.includes(id))
  );

  // Dynamic: get all categories that have articles, sorted by order
  const catsWithArticles = categories
    .filter(c => byCat(c.category_id).length > 0)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Split categories into groups for layout variety
  const firstCat = catsWithArticles[0];
  const secondCat = catsWithArticles[1];
  const remainingCats = catsWithArticles.slice(2);

  // Homepage section ordering from admin settings (keep only enabled sections)
  const sectionConfig = Array.isArray(settings?.homepage_sections) ? settings.homepage_sections : [];
  const orderedSections = sectionConfig
    .filter(s => s && s.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Slider is now embedded INSIDE the HeroSection (main left panel).
  // The old standalone "slider" homepage_sections id is a no-op.
  const sliderCfg = settings?.slider || {};

  const renderSection = (sec) => {
    switch (sec.id) {
      case 'breaking_ticker':
        return <Ticker key="breaking_ticker" settings={settings?.breaking_news} />;
      case 'election_panel':
        return <ElectionPanel key="election_panel" config={settings?.election_panel} />;
      case 'widgets_bar':
        return <WidgetsBar key="widgets_bar" stocks={stocks} cricket={cricket} />;
      case 'slider':
        // Legacy no-op: slider now lives inside hero
        return null;
      case 'hero':
        return <HeroSection key="hero" articles={articles} categories={categories} sliderCfg={sliderCfg} />;
      case 'category_first':
        return firstCat ? (
          <CategorySection
            key="category_first"
            title={isKannada ? (firstCat.name_kn || firstCat.name) : firstCat.name}
            articles={byCat(firstCat.category_id)}
            categories={categories}
            catId={firstCat.category_id}
          />
        ) : null;
      case 'category_popular':
        return secondCat ? (
          <NationalWithPopular
            key="category_popular"
            title={isKannada ? (secondCat.name_kn || secondCat.name) : secondCat.name}
            catId={secondCat.category_id}
            articles={byCat(secondCat.category_id)}
            categories={categories}
            allArticles={articles}
          />
        ) : null;
      case 'video_shorts':
        return <VideoSection key="video_shorts" shorts={shorts} />;
      case 'category_rest':
        return (
          <div key="category_rest">
            {remainingCats.map(cat => (
              <CategorySection
                key={cat.category_id}
                title={isKannada ? (cat.name_kn || cat.name) : cat.name}
                articles={byCat(cat.category_id)}
                categories={categories}
                catId={cat.category_id}
              />
            ))}
          </div>
        );
      case 'podcasts':
        return <PodcastSection key="podcasts" podcasts={podcasts} />;
      default: {
        // Dynamic per-category sections: id like "category_<cat_id>".
        // This makes the "Other Categories" block fully individually draggable
        // and auto-extends when a new category is added.
        if (typeof sec.id === 'string' && sec.id.startsWith('category_')) {
          const catId = sec.id.replace('category_', '');
          const cat = categories.find(c => c.category_id === catId);
          if (!cat) return null;
          return (
            <CategorySection
              key={sec.id}
              title={isKannada ? (cat.name_kn || cat.name) : cat.name}
              articles={byCat(cat.category_id)}
              categories={categories}
              catId={cat.category_id}
            />
          );
        }
        return null;
      }
    }
  };

  // Breaking ticker is full-width (outside the container). Split rendering.
  const isFullWidth = (id) => id === 'breaking_ticker' || id === 'election_panel';
  const fullWidthSections = orderedSections.filter(s => isFullWidth(s.id));
  const innerSections = orderedSections.filter(s => !isFullWidth(s.id));

  // If settings returned no section config, fall back to the full default list
  const hasConfig = orderedSections.length > 0;
  const defaultOrder = [
    { id: 'breaking_ticker', enabled: true, order: 0 },
    { id: 'election_panel', enabled: true, order: 1 },
    { id: 'widgets_bar', enabled: true, order: 2 },
    { id: 'slider', enabled: true, order: 3 },
    { id: 'hero', enabled: true, order: 4 },
    { id: 'category_first', enabled: true, order: 5 },
    { id: 'category_popular', enabled: true, order: 6 },
    { id: 'video_shorts', enabled: true, order: 7 },
    { id: 'category_rest', enabled: true, order: 8 },
    { id: 'podcasts', enabled: true, order: 9 },
  ];
  const finalFullWidth = hasConfig ? fullWidthSections : defaultOrder.filter(s => isFullWidth(s.id));
  const finalInner = hasConfig ? innerSections : defaultOrder.filter(s => !isFullWidth(s.id));

  // Always render election panel above hero if enabled, even if not in section list
  const electionInList = finalFullWidth.some(s => s.id === 'election_panel');
  const electionEnabled = settings?.election_panel?.enabled === true;

  return (
    <div className="bg-[#f8f8f8] min-h-screen" data-testid="homepage">
      {finalFullWidth.map(renderSection)}
      {/* Auto-render Election Panel above hero if enabled but not yet in section list */}
      {!electionInList && electionEnabled && <ElectionPanel config={settings.election_panel} />}

      <div className="max-w-[1400px] mx-auto px-4 pb-10">
        {finalInner.map(renderSection)}

        {/* Fallback if no categorized articles */}
        {catsWithArticles.length === 0 && articles.length > 0 && (
          <CategorySection
            title={isKannada ? 'ಎಲ್ಲಾ ಸುದ್ದಿಗಳು' : 'All News'}
            articles={articles.slice(7)}
            categories={categories}
          />
        )}
      </div>
    </div>
  );
}
